import type {
  BackupDocument,
  BackupStore,
  LocalBackupGateway,
} from "../../application/ports/backup";
import { parseBackupJson, validateBackupEnvelope } from "./validation";
import type { BackupEnvelope } from "./backup-document";
import type { LocalBackupManager } from "./local-backup-manager";
import type { GoogleDriveBackupStore } from "../google-drive/drive-backup-store";

export class IndexedDbLocalBackupGateway implements LocalBackupGateway {
  constructor(private readonly manager: LocalBackupManager) {}

  async createDocument(): Promise<BackupDocument> {
    return toApplicationDocument(await this.manager.create());
  }

  async validate(document: BackupDocument): Promise<void> {
    await extractEnvelope(document);
  }

  async replaceFromWithSafetyBackup(
    document: BackupDocument,
    uploadSafetyBackup: (document: BackupDocument) => Promise<void>,
  ): Promise<void> {
    const envelope = await extractEnvelope(document);
    await this.manager.restoreWithSafetyBackup(
      envelope,
      async (safetyBackup) => uploadSafetyBackup(toApplicationDocument(safetyBackup)),
    );
  }
}

export class GoogleDriveApplicationBackupStore implements BackupStore {
  constructor(
    private readonly drive: GoogleDriveBackupStore,
    private readonly localManager?: LocalBackupManager,
    private readonly retention?: number,
  ) {}

  async upload(document: BackupDocument): Promise<void> {
    const envelope = await extractEnvelope(document);
    try {
      await this.drive.upload(envelope);
      await this.localManager?.markUploaded(envelope);
      // Retention failure should not turn a successfully uploaded backup into a
      // false failure, so cleanup is best-effort.
      if (this.retention !== undefined) void this.drive.retainNewest(this.retention).catch(() => undefined);
    } catch (error) {
      await this.localManager?.markUploadFailed(error instanceof Error ? error.message : "Google Drive backup failed");
      throw error;
    }
  }

  async downloadLatest(): Promise<BackupDocument | undefined> {
    const [latest] = await this.drive.list();
    if (!latest) return undefined;
    const parsed = parseBackupJson(await this.drive.download(latest.id));
    const envelope = await validateBackupEnvelope(parsed);
    return toApplicationDocument(envelope);
  }
}

export function toApplicationDocument(envelope: BackupEnvelope): BackupDocument {
  return {
    formatVersion: envelope.formatVersion,
    backupId: envelope.backupId,
    createdAt: envelope.createdAt,
    appVersion: envelope.appVersion,
    revision: envelope.revision,
    checksum: envelope.checksum,
    data: envelope,
  };
}

async function extractEnvelope(document: BackupDocument): Promise<BackupEnvelope> {
  const envelope = await validateBackupEnvelope(document.data);
  if (
    envelope.formatVersion !== document.formatVersion ||
    envelope.backupId !== document.backupId ||
    envelope.createdAt !== document.createdAt ||
    envelope.appVersion !== document.appVersion ||
    envelope.revision !== document.revision ||
    envelope.checksum !== document.checksum
  ) {
    throw new Error("Backup document metadata does not match its signed data");
  }
  return envelope;
}
